import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu as MenuIcon, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-stone-800 bg-stone-900/95 text-stone-100 backdrop-blur-md shadow-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/logo.png" alt="Flavors Nigerian Restaurant" className="h-11 w-11 rounded-lg object-cover ring-1 ring-amber-500/40" />
          <div className="leading-tight">
            <div className="font-display text-base font-extrabold text-amber-400 sm:text-lg">Flavors Nigerian Restaurant</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone-400">Orlando</div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 hover:bg-stone-800 hover:text-amber-400 ${
                loc.pathname === l.to ? "text-amber-400" : "text-stone-200"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="header-login-button" className="hidden sm:block">
            <Button variant="ghost" size="icon" className="text-stone-200 hover:bg-stone-800 hover:text-amber-400">
              <User className="h-5 w-5" />
            </Button>
          </Link>
          <button
            data-testid="header-cart-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 rounded-full bg-orange-700 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-orange-800"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-xs font-bold text-stone-900">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-stone-200 hover:bg-stone-800 lg:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="border-t border-stone-800 bg-stone-900 lg:hidden" data-testid="mobile-nav">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className={`rounded-lg px-4 py-3 text-sm font-semibold transition-colors duration-200 hover:bg-stone-800 hover:text-amber-400 ${
                  loc.pathname === l.to ? "text-amber-400" : "text-stone-200"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/login" onClick={() => setMobileOpen(false)} className="rounded-lg px-4 py-3 text-sm font-semibold text-stone-200 hover:bg-stone-800 hover:text-amber-400">
              Staff Login
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
